// src/services/imageProcessing.js

const DEFAULTS = {
  maxInputBytes: 25 * 1024 * 1024,   
  maxOutputBytes: 1_500_000,         
  maxPixels: 40_000_000,             
  preferMime: 'image/jpeg',
  startQuality: 0.85,
  minQuality: 0.55,
};

function isHeic(file) {
  const t = (file?.type || '').toLowerCase();
  const n = (file?.name || '').toLowerCase();
  return t.includes('heic') || t.includes('heif') || n.endsWith('.heic') || n.endsWith('.heif');
}

async function decodeImage(file) {
  return await createImageBitmap(file, { imageOrientation: 'from-image' });
}

async function maybeConvertHeic(file) {
  if (!isHeic(file)) return file;

  try {
    await decodeImage(file);
    return file;
  } catch (e) {
    const mod = await import('heic2any');
    const heic2any = mod.default || mod;
    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    return new File([blob], (file.name || 'photo').replace(/\.(heic|heif)$/i, '.jpg'), {
      type: 'image/jpeg'
    });
  }
}

async function canvasToBlob(canvas, mime, quality) {
  return await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))), mime, quality);
  });
}

export async function processImageFile(inputFile, opts = {}) {
  const cfg = { ...DEFAULTS, ...opts };
  if (!inputFile) throw new Error('NO_FILE');

  if (inputFile.size > cfg.maxInputBytes) {
    const err = new Error('INPUT_TOO_LARGE');
    err.code = 'INPUT_TOO_LARGE';
    err.maxBytes = cfg.maxInputBytes;
    throw err;
  }

  const file = await maybeConvertHeic(inputFile);

  let bitmap;
  try {
    bitmap = await decodeImage(file);
  } catch (e) {
    const err = new Error('DECODE_FAILED');
    err.code = 'DECODE_FAILED';
    throw err;
  }

  const w0 = bitmap.width;
  const h0 = bitmap.height;

  if (w0 * h0 > cfg.maxPixels) {
    bitmap.close?.();
    const err = new Error('TOO_MANY_PIXELS');
    err.code = 'TOO_MANY_PIXELS';
    err.pixels = w0 * h0;
    err.maxPixels = cfg.maxPixels;
    throw err;
  }

  const needsResize = Math.max(w0, h0) > cfg.maxDim;
  const needsBytes = file.size > cfg.maxOutputBytes;

  const safeTypes = ['image/jpeg', 'image/png', 'image/webp'];
  if (!needsResize && !needsBytes && safeTypes.includes((file.type || '').toLowerCase())) {
    bitmap.close?.();
    return { file, width: w0, height: h0, compressed: false };
  }

  const scale = Math.min(1, cfg.maxDim / Math.max(w0, h0));
  let w = Math.max(1, Math.round(w0 * scale));
  let h = Math.max(1, Math.round(h0 * scale));

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = w;
  canvas.height = h;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, w, h);

  bitmap.close?.();

  let quality = cfg.startQuality;
  let blob = await canvasToBlob(canvas, cfg.preferMime, quality);

  while (blob.size > cfg.maxOutputBytes && quality > cfg.minQuality) {
    quality = Math.max(cfg.minQuality, quality - 0.08);
    blob = await canvasToBlob(canvas, cfg.preferMime, quality);
  }

  while (blob.size > cfg.maxOutputBytes && Math.max(w, h) > 900) {
    w = Math.max(1, Math.round(w * 0.85));
    h = Math.max(1, Math.round(h * 0.85));
    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(await createImageBitmap(blob), 0, 0, w, h);
    quality = Math.max(cfg.minQuality, quality - 0.05);
    blob = await canvasToBlob(canvas, cfg.preferMime, quality);
  }

  if (blob.size > cfg.maxOutputBytes) {
    const err = new Error('OUTPUT_TOO_LARGE');
    err.code = 'OUTPUT_TOO_LARGE';
    err.bytes = blob.size;
    err.maxBytes = cfg.maxOutputBytes;
    throw err;
  }

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  const outName = `${baseName}.jpg`;
  const outFile = new File([blob], outName, { type: cfg.preferMime });

  return { file: outFile, width: w0, height: h0, compressed: true };
}
